import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Loader2, Wrench, LayoutList, LayoutGrid, ChevronDown, ChevronUp, Cog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SparesOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allSpares, setAllSpares] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [machineFilter, setMachineFilter] = useState("all");
  const [viewMode, setViewMode] = useState("flat"); // "flat" or "grouped"
  const [expandedMachines, setExpandedMachines] = useState({});

  useEffect(() => { fetchOverview(); }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/spares-overview`);
      setAllSpares(res.data);
    } catch (err) {
      toast.error("Failed to load spares overview");
    } finally {
      setLoading(false);
    }
  };

  // Get unique machines for filter
  const machines = useMemo(() => {
    const map = new Map();
    allSpares.forEach((s) => {
      if (!map.has(s.machine_id)) map.set(s.machine_id, s.machine_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [allSpares]);

  // Filter spares
  const filtered = useMemo(() => {
    return allSpares.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (machineFilter !== "all" && s.machine_id !== machineFilter) return false;
      if (searchFilter) {
        const q = searchFilter.toLowerCase();
        return (
          s.spare_name?.toLowerCase().includes(q) ||
          s.spare_id?.toLowerCase().includes(q) ||
          s.machine_name?.toLowerCase().includes(q) ||
          s.machine_id?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allSpares, searchFilter, statusFilter, machineFilter]);

  // Group by machine
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      if (!map[s.machine_id]) map[s.machine_id] = { machine_id: s.machine_id, machine_name: s.machine_name, spares: [] };
      map[s.machine_id].spares.push(s);
    });
    return Object.values(map).sort((a, b) => a.machine_id.localeCompare(b.machine_id));
  }, [filtered]);

  const toggleMachineGroup = (machineId) => {
    setExpandedMachines((prev) => ({ ...prev, [machineId]: !prev[machineId] }));
  };

  // Stats
  const available = filtered.filter((s) => s.status === "Available").length;
  const inUse = filtered.filter((s) => s.status === "In Use").length;
  const damaged = filtered.filter((s) => s.status === "Damaged").length;

  const statusBadge = (status) => {
    const styles = {
      Available: "bg-green-500/20 text-green-400 border-green-500/30",
      "In Use": "bg-blue-500/20 text-blue-400 border-blue-500/30",
      Damaged: "bg-red-500/20 text-red-400 border-red-500/30",
      "Out of Stock": "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles["Out of Stock"]}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/workorder/machines")} className="text-gray-400 hover:text-white" data-testid="back-to-machines-overview">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8 text-amber-400" />
              <h1 className="text-2xl font-bold text-white">Spares Overview</h1>
              <Badge variant="secondary" className="bg-gray-700 text-gray-300">{filtered.length} Spares</Badge>
            </div>
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-1">
            <Button
              variant={viewMode === "flat" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("flat")}
              className={viewMode === "flat" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"}
              data-testid="view-flat-btn"
            >
              <LayoutList className="h-4 w-4 mr-1" /> Flat Table
            </Button>
            <Button
              variant={viewMode === "grouped" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grouped")}
              className={viewMode === "grouped" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"}
              data-testid="view-grouped-btn"
            >
              <LayoutGrid className="h-4 w-4 mr-1" /> Grouped
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gray-800/50 border border-amber-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg"><Wrench className="h-6 w-6 text-amber-400" /></div>
              <div><p className="text-xl font-bold text-white">{filtered.length}</p><p className="text-xs text-gray-400">Total</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-green-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg"><Wrench className="h-6 w-6 text-green-400" /></div>
              <div><p className="text-xl font-bold text-white">{available}</p><p className="text-xs text-gray-400">Available</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-blue-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg"><Wrench className="h-6 w-6 text-blue-400" /></div>
              <div><p className="text-xl font-bold text-white">{inUse}</p><p className="text-xs text-gray-400">In Use</p></div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-red-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg"><Wrench className="h-6 w-6 text-red-400" /></div>
              <div><p className="text-xl font-bold text-white">{damaged}</p><p className="text-xs text-gray-400">Damaged</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800/50 border border-gray-700 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Search by spare name, ID, or machine..." className="bg-gray-900 border-gray-600 text-white pl-10" data-testid="overview-search" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-sm whitespace-nowrap">Status:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36 bg-gray-900 border-gray-600 text-white" data-testid="overview-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="In Use">In Use</SelectItem>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                    <SelectItem value="Out of Stock">Out of Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-sm whitespace-nowrap">Machine:</Label>
                <Select value={machineFilter} onValueChange={setMachineFilter}>
                  <SelectTrigger className="w-48 bg-gray-900 border-gray-600 text-white" data-testid="overview-machine-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="all">All Machines</SelectItem>
                    {machines.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.id} - {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {viewMode === "flat" ? (
          /* FLAT TABLE VIEW */
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader className="border-b border-gray-700">
              <CardTitle className="text-lg text-amber-400">All Spares — Flat View</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-amber-400">Spare ID</TableHead>
                    <TableHead className="text-amber-400">Spare Name</TableHead>
                    <TableHead className="text-amber-400">Machine ID</TableHead>
                    <TableHead className="text-amber-400">Machine Name</TableHead>
                    <TableHead className="text-amber-400 text-center">Quantity</TableHead>
                    <TableHead className="text-amber-400 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                        {searchFilter || statusFilter !== "all" || machineFilter !== "all"
                          ? "No spares match your filters."
                          : "No spares assigned to any machine yet."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((spare) => (
                      <TableRow key={`${spare.machine_id}-${spare.id}`} className="border-gray-700">
                        <TableCell>
                          <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                            {spare.spare_id}
                          </span>
                        </TableCell>
                        <TableCell className="text-white font-medium">{spare.spare_name || spare.name}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                            {spare.machine_id}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-300">{spare.machine_name}</TableCell>
                        <TableCell className="text-center text-gray-300">{spare.quantity}</TableCell>
                        <TableCell className="text-center">{statusBadge(spare.status)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          /* GROUPED BY MACHINE VIEW */
          <div className="space-y-4">
            {grouped.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Wrench className="mx-auto h-12 w-12 text-gray-600" />
                  <p className="text-gray-400 mt-4">No spares match your filters.</p>
                </CardContent>
              </Card>
            ) : (
              grouped.map((group) => {
                const isExpanded = expandedMachines[group.machine_id] !== false; // default expanded
                return (
                  <Card key={group.machine_id} className="bg-gray-800/50 border-gray-700">
                    <CardHeader
                      className="cursor-pointer hover:bg-gray-700/30 transition-all border-b border-gray-700 py-3"
                      onClick={() => toggleMachineGroup(group.machine_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Cog className="h-5 w-5 text-cyan-400" />
                          <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-mono font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                            {group.machine_id}
                          </span>
                          <CardTitle className="text-base text-white">{group.machine_name}</CardTitle>
                          <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                            {group.spares.length} spare{group.spares.length !== 1 ? "s" : ""}
                          </Badge>
                        </div>
                        {isExpanded ? <ChevronUp className="text-gray-400" /> : <ChevronDown className="text-gray-400" />}
                      </div>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent className="pt-3 pb-2">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-gray-600">
                              <TableHead className="text-amber-400 text-xs">Spare ID</TableHead>
                              <TableHead className="text-amber-400 text-xs">Spare Name</TableHead>
                              <TableHead className="text-amber-400 text-xs text-center">Quantity</TableHead>
                              <TableHead className="text-amber-400 text-xs text-center">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.spares.map((spare) => (
                              <TableRow key={spare.id} className="border-gray-600/50">
                                <TableCell>
                                  <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                                    {spare.spare_id}
                                  </span>
                                </TableCell>
                                <TableCell className="text-white text-sm">{spare.spare_name || spare.name}</TableCell>
                                <TableCell className="text-center text-gray-300 text-sm">{spare.quantity}</TableCell>
                                <TableCell className="text-center">{statusBadge(spare.status)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SparesOverview;
